import { useSubscriptionMap } from 'api/goodtrust/subscription'
import { Button } from 'components/button/Button'
import { directivePaywallContinue } from 'components/directive/utils/directivePaywallContinue'
import { Icon } from 'components/icon'
import { PerkItem } from 'components/perkItem/PerkItem'
import { useSafeFormContext } from 'components/SafeForm'
import { Spacer } from 'components/spacer/Spacer'
import { Text, Title } from 'components/Typography'
import { describeSubscriptions } from 'logic/subscription/describe'
import { getPerksDirective } from 'logic/subscription/plan/spec'
import { useDirectiveUpsellPlanType } from 'logic/subscription/upsell/useDirectiveUpsellPlanType'
import { useTranslation } from 'next-i18next'
import { ReactNode, VFC } from 'react'
import { events } from 'utils/analytics'
import { EventArgs } from 'utils/analytics/types'
import { LogoOfTheDay } from 'utils/cobranding/LogoOfTheDay'
import { encodeQuery } from 'utils/fetcher'
import { formatPriceInCents, withDollarSign } from 'utils/format'
import { safeWindow } from 'utils/general'
import { useRunOnceReady } from 'utils/hooks'
import {
  SButtonGrid,
  SDivider,
  SHeaderSection,
  SHeaderTextStack,
  SPerkGrid,
  SPictureWrapper,
  SUpsellModal,
} from './UpsellModal.styled'

export type UpsellModalProps = {
  title: ReactNode
  primaryButtonText: ReactNode
  picture: ReactNode
  secondaryButtonText?: ReactNode
  secondaryButton?: ReactNode
  paywallHitEventArgs: EventArgs['directive_paywall_hit'] | 'no-trigger'
  close: () => void
  buttonOrientation: 'horizontal' | 'vertical'
}

export const UpsellModal: VFC<UpsellModalProps> = (props) => {
  const { t: tDirective } = useTranslation('directive')
  const subMap = useSubscriptionMap()
  const planType = useDirectiveUpsellPlanType()
  const perks = planType ? getPerksDirective(tDirective, planType) : []
  const { getEstatePlanningPrices } = describeSubscriptions(subMap.data?.json ?? {})

  const hasLoadedData = subMap.data
  const safeFormCtx = useSafeFormContext()

  useRunOnceReady(true, () => {
    if (props.paywallHitEventArgs === 'no-trigger') return
    events.directive_paywall_hit.fire(props.paywallHitEventArgs)
  })

  return (
    <SUpsellModal>
      <LogoOfTheDay />

      <Spacer size={1.5} />

      <SHeaderSection>
        <SHeaderTextStack space={'1rem'}>
          <Title variant={'h7'}>{props.title}</Title>
          <Text variant={'bodyLarge'}>
            <Icon name="ShieldCheck" size={16} css={{ marginRight: 12 }} />
            {tDirective('directive.tips.upsell.price_line', {
              price: withDollarSign(
                formatPriceInCents(getEstatePlanningPrices().initial_price ?? 0)
              ),
            })}
          </Text>
        </SHeaderTextStack>
        <SPictureWrapper>{props.picture}</SPictureWrapper>
      </SHeaderSection>

      <Spacer size={1.5} />

      <SDivider />

      <Spacer size={1} />

      <SPerkGrid>
        {perks.map((perk, i) => (
          <PerkItem key={i} perk={perk} />
        ))}
      </SPerkGrid>

      <Spacer size={2} />

      <SButtonGrid orientation={props.buttonOrientation}>
        {props.secondaryButtonText ? (
          <Button
            type="button"
            size={['medium', 'large']}
            variant={'secondary'}
            tone={'dark'}
            onClick={props.close}
            loading={!hasLoadedData}
          >
            {props.secondaryButtonText}
          </Button>
        ) : (
          props.secondaryButton
        )}
        <Button
          type="button"
          loading={!hasLoadedData}
          size={'large'}
          href={encodeQuery('/signup/checkout', { next: safeWindow?.location.pathname })}
          onClick={() => {
            safeFormCtx?.disableWarningOnExit()
            if (planType) directivePaywallContinue(planType)
          }}
          css={{ textAlign: 'center' }}
        >
          {props.primaryButtonText}
        </Button>
      </SButtonGrid>
    </SUpsellModal>
  )
}
